import { AgentCategory } from "~/shared/enums";
import type { AgentConfig } from "~/shared/types";
import { makeValidateRegex, validateGithubRepository } from "~/utils/validation";

import AgentIcon from "./AgentIcon.vue";
import Icon from "./Icon.vue";

const TOKEN_REGEX = /^(ghp_|gho_|ghu_|ghs_|ghr_|github_pat_)[A-Za-z0-9_]+$/;
const validateToken = makeValidateRegex(TOKEN_REGEX, "Enter a valid GitHub token", true);

const COPILOT_MODEL_DEFAULT_VALUE = "auto";
const COPILOT_MODEL_OPTIONS = [
  { label: "Auto", value: COPILOT_MODEL_DEFAULT_VALUE },
  { label: "GPT-5.1-Codex-Max", value: "gpt-5.1-codex-max" },
  { label: "Claude Opus 4.5", value: "claude-opus-4.5" },
  { label: "Claude Sonnet 4.5", value: "claude-sonnet-4.5" },
];

const config: AgentConfig = {
  name: "Copilot",
  category: AgentCategory.CODING,
  icon: Icon,
  agentIcon: AgentIcon,
  color: "#8534F3",
  helpUrl: "https://help.dartai.com/en/articles/13893461-ai-coding-agent-with-github-copilot",
  description: "Delegate issues to Copilot and get draft pull requests you can inspect, refine, and merge",
  descriptionMarkdown: `## Overview

Delegate implementation work to GitHub Copilot coding agent directly from Dart. When you assign a task to this agent, Dart creates a GitHub issue in your configured repository and assigns it to \`copilot-swe-agent[bot]\`.

Copilot then opens a draft pull request and iterates based on feedback in GitHub. Use this integration when you want planning in Dart and execution in a GitHub-native Copilot workflow.

## Setup guide

1. Confirm your GitHub account has a Copilot plan that includes Copilot coding agent access
2. Enable Copilot coding agent for the target repository in GitHub Copilot settings/policies
3. Create a GitHub token: \`repo\` scope for classic PAT, or fine-grained PAT with metadata read plus actions, contents, issues, and pull requests read/write permissions
4. Open Dart settings, go to Agents, and choose Copilot
5. Paste your token and set GitHub repo (accepted formats: \`owner/repo\`, \`https://github.com/owner/repo\`, or \`git@github.com:owner/repo.git\`)
6. Assign coding tasks to the Copilot agent in Dart`,

  configFields: [
    {
      key: "apiKey",
      type: "password",
      label: "GitHub token",
      placeholder: "Your GitHub personal access token",
      validate: validateToken,
    },
    {
      key: "repository",
      type: "text",
      label: "GitHub repo",
      placeholder: "owner/repository",
      validate: validateGithubRepository,
    },
    {
      key: "model",
      type: "select",
      label: "Model",
      placeholder: "Select a model",
      options: COPILOT_MODEL_OPTIONS,
      defaultValue: COPILOT_MODEL_DEFAULT_VALUE,
    },
    // TODO temporarily removed these from FE to simplify, either enable or remove from FE and BE
    // {
    //   key: "baseBranch",
    //   type: "text",
    //   label: "Base branch (optional)",
    //   placeholder: "main",
    // },
    // {
    //   key: "instructions",
    //   type: "text",
    //   label: "Custom instructions (optional)",
    //   placeholder: "Prioritize tests and keep diffs small",
    // },
    // {
    //   key: "customAgent",
    //   type: "text",
    //   label: "Custom agent (optional)",
    //   placeholder: "Leave blank for Copilot default",
    // },
  ],
};

export default config;
